/**
 * <km-film> — Muted, looping YouTube embed with a sound toggle.
 *
 * Browsers only allow autoplay while muted, so the film starts silent and the
 * [data-km-film-sound] button unmutes/mutes it through the YouTube iframe API
 * (postMessage, requires enablejsapi=1 on the embed src).
 *
 * Required child markup:
 *   iframe[data-km-film-player] — the YouTube embed.
 *   button[data-km-film-sound]  — the toggle; its label reflects the current state.
 *
 * Element dataset:
 *   data-label-on  — label shown while sound is playing.
 *   data-label-off — label shown while muted.
 */
class KmFilm extends HTMLElement {
  #ac;
  #player;
  #button;
  #soundOn = false;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#player = this.querySelector('[data-km-film-player]');
    this.#button = this.querySelector('[data-km-film-sound]');
    if (!this.#player || !this.#button) return;
    this.#button.addEventListener('click', () => this.#toggle(), { signal: this.#ac.signal });
    this.#observeReveal();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  // With [data-reveal], the film stays covered and paused until most of it is on screen.
  #observeReveal() {
    if (!this.hasAttribute('data-reveal') || !('IntersectionObserver' in window)) {
      this.removeAttribute('data-hidden');
      return;
    }
    this.#observer = new IntersectionObserver(([entry]) => {
      const inView = entry.intersectionRatio >= 0.6;
      this.toggleAttribute('data-hidden', !inView);
      this.#command(inView ? 'playVideo' : 'pauseVideo');
    }, { threshold: [0, 0.6, 1] });
    this.#observer.observe(this);
    this.#player.addEventListener('load', () => {
      if (this.hasAttribute('data-hidden')) this.#command('pauseVideo');
    }, { signal: this.#ac.signal });
  }

  #command(func, args = []) {
    this.#player.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args }), '*');
  }

  #toggle() {
    this.#soundOn = !this.#soundOn;
    if (this.#soundOn) {
      this.#command('unMute');
      this.#command('setVolume', [100]);
      this.#command('playVideo');
    } else {
      this.#command('mute');
    }
    this.#button.setAttribute('aria-pressed', String(this.#soundOn));
    this.#button.textContent = this.#soundOn ? this.dataset.labelOn : this.dataset.labelOff;
  }
}

if (!customElements.get('km-film')) customElements.define('km-film', KmFilm);
